import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Heart, Shield, Lock } from "lucide-react";

const AutoFitNumber = ({ value, testid }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.fontSize = "72px";
    const parent = el.parentElement;
    if (!parent) return;
    const avail = parent.clientWidth - 8;
    // shrink until the digit span fits
    let size = Math.min(72, parent.clientWidth * 0.42);
    el.style.fontSize = `${size}px`;
    for (let i = 0; i < 30 && el.scrollWidth > avail && size > 20; i++) {
      size -= 2;
      el.style.fontSize = `${size}px`;
    }
  }, [value]);
  return (
    <div className="font-display mt-2 w-full text-center text-white font-black tabular-nums leading-none overflow-hidden">
      <span data-testid={testid} ref={ref} className="inline-block">{Number(value).toLocaleString("en-IN")}</span>
    </div>
  );
};

const Counter = ({ label, value, tone }) => (
  <div
    data-testid={`${label.toLowerCase()}-counter-card`}
    className={`counter-card glass rounded-3xl px-5 py-7 flex-1 min-w-0 ${tone}`}
  >
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-purple-200/80">
      <span className="live-dot" /> {label}
    </div>
    <AutoFitNumber value={value} testid={`${label.toLowerCase()}-counter-value`} />
  </div>
);

export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [counts, setCounts] = useState({ boy: null, girl: null });

  useEffect(() => {
    let ws;
    let alive = true;
    const fetchOnce = async () => {
      try { const { data } = await api.get("/counts"); if (alive) setCounts({ boy: data.boy, girl: data.girl }); } catch {}
    };
    fetchOnce();
    try {
      const wsUrl = BACKEND_URL.replace(/^http/, "ws") + "/api/ws/counts";
      ws = new WebSocket(wsUrl);
      ws.onmessage = (ev) => {
        try { const m = JSON.parse(ev.data); if (m.type === "counts") setCounts({ boy: m.boy, girl: m.girl }); } catch {}
      };
    } catch {}
    const i = setInterval(fetchOnce, 10000);
    return () => { alive = false; clearInterval(i); try { ws && ws.close(); } catch {} };
  }, []);

  const onJoin = () => {
    if (user) nav("/dashboard"); else nav("/register");
  };

  return (
    <div className="min-h-screen bg-dc-gradient flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-xl">
          <div data-testid="home-hero-card" className="glass-strong rounded-[36px] px-6 sm:px-10 py-10 sm:py-12 text-center">
            <div className="font-display text-4xl sm:text-5xl font-black tracking-[0.08em] text-white">
              DILCONNECT
            </div>
            <div className="mt-3 flex justify-center">
              <span data-testid="age-badge" className="pill-18 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold tracking-[0.3em]">
                <Shield className="w-3.5 h-3.5" /> 18+ ONLY
              </span>
            </div>
            <h1 className="font-display mt-7 text-3xl sm:text-4xl font-extrabold text-white">
              Find Your Connection
            </h1>
            <p className="mt-2 text-sm text-purple-200/70">Private • Mutual • Real</p>

            <div className="mt-7 flex gap-4">
              <Counter label="BOYS" value={counts.boy ?? "—"} tone="counter-boy" />
              <Counter label="GIRLS" value={counts.girl ?? "—"} tone="counter-girl" />
            </div>

            <button
              data-testid="join-dilconnect-button"
              onClick={onJoin}
              className="btn-cta mt-8 w-full rounded-2xl text-lg py-4 flex items-center justify-center gap-2"
            >
              <Heart className="w-5 h-5" /> Join DilConnect
            </button>

            <div className="mt-5 flex items-center justify-center gap-2 text-xs text-purple-200/70">
              <Lock className="w-3.5 h-3.5" /> Private • 18+ Only • Mutual Matching
            </div>

            {!user && (
              <div className="mt-5 text-xs text-purple-200/60">
                Already a member? <Link to="/login" data-testid="home-login-link" className="text-white font-semibold hover:underline">Log in</Link>
              </div>
            )}
          </div>

          <div className="mt-6 text-center text-[11px] text-purple-200/50 space-x-3">
            <Link data-testid="footer-terms" to="/terms" className="hover:text-white">Terms</Link>
            <span>•</span>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <span>•</span>
            <Link to="/refund" className="hover:text-white">Refund</Link>
            <span>•</span>
            <Link to="/18-plus" className="hover:text-white">18+ Policy</Link>
            <span>•</span>
            <Link to="/safety" className="hover:text-white">Safety</Link>
            <span>•</span>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
